import React, { useEffect, useRef, useState } from 'react';
import MessageBubble from './MessageBubble';
import { LoadingSpinner } from '../common/LoadingSpinner';

/**
 * MessageList - Scrollable message container with infinite scroll
 *
 * @param {Object} props
 * @param {Array} props.messages - Array of message objects
 * @param {Function} props.onLoadMore - Callback to load older messages
 * @param {boolean} props.hasMore - Whether there are more messages to load
 * @param {boolean} props.isLoading - Whether messages are currently loading
 * @param {Object} props.messagesEndRef - Ref to scroll to bottom
 */
const MessageList = ({ messages, onLoadMore, hasMore, isLoading, messagesEndRef }) => {
  const containerRef = useRef(null);
  const [previousScrollHeight, setPreviousScrollHeight] = useState(0);
  const loadTriggerRef = useRef(null);
  const internalEndRef = useRef(null);

  // Use provided ref or internal ref
  const endRef = messagesEndRef || internalEndRef;

  /**
   * Auto-scroll to bottom when messages change
   */
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  /**
   * Intersection Observer for infinite scroll
   * Triggers when user scrolls near the top to load older messages
   */
  useEffect(() => {
    if (!loadTriggerRef.current || !hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !isLoading) {
          // Save current scroll position
          if (containerRef.current) {
            setPreviousScrollHeight(containerRef.current.scrollHeight);
          }
          onLoadMore();
        }
      },
      {
        root: containerRef.current,
        rootMargin: '100px',
        threshold: 0.1
      }
    );

    observer.observe(loadTriggerRef.current);

    return () => {
      if (loadTriggerRef.current) {
        observer.unobserve(loadTriggerRef.current);
      }
    };
  }, [hasMore, isLoading, onLoadMore]);

  /**
   * Maintain scroll position when loading older messages
   */
  useEffect(() => {
    if (containerRef.current && previousScrollHeight > 0) {
      const newScrollHeight = containerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - previousScrollHeight;
      containerRef.current.scrollTop = scrollDiff;
      setPreviousScrollHeight(0);
    }
  }, [messages, previousScrollHeight]);

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center">
          <svg
            className="w-16 h-16 text-gray-300 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <p className="text-gray-500 text-lg mb-2">No messages yet</p>
          <p className="text-gray-400 text-sm">
            Start a conversation to practice your vocabulary!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-full overflow-y-auto px-4 py-6 space-y-4"
      style={{ scrollBehavior: 'smooth' }}
    >
      {/* Load more trigger (at top) */}
      {hasMore && (
        <div ref={loadTriggerRef} className="h-1" />
      )}

      {/* Loading indicator for loading more messages */}
      {isLoading && hasMore && (
        <div className="flex justify-center py-4">
          <LoadingSpinner size="small" />
        </div>
      )}

      {/* Messages */}
      {messages.map((message) => (
        <MessageBubble
          key={message.messageId}
          message={message}
          showTimestamp={true}
        />
      ))}

      {/* Scroll anchor (bottom) */}
      <div ref={endRef} />
    </div>
  );
};

export default MessageList;
